import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { api } from '../api/cliente';
import { useSessao } from '../auth/SessaoContext';
import { progressoGeral } from '../paginas/mundo/progressoGeral';
import type { Progresso } from '../paginas/mundo/tipos';
import { CHAVE_QUADRO } from '../paginas/devboard/tipos';
import type { Quadro } from '../paginas/devboard/tipos';
import { CHAVE_ROADMAP, SLUG } from '../paginas/roadmapApi';
import type { Roadmap } from '../api/tipos';
import { IconeDoMenu } from './IconeDoMenu';
import { AvatarComNotificacoes } from './Notificacoes';
import type { ItemNav } from './menu';
import { Avatar } from '../comum/Avatar';
import { destinoDa, SECOES } from './menu';
import { SeletorDeProjeto } from './SeletorDeProjeto';
import { TelaQueQuebrou } from './TelaQueQuebrou';
import './shell.css';

/** Onde a barra lembra quais seções o time deixou fechadas. */
const SECOES_FECHADAS = 'ggpilot-secoes-fechadas';

/**
 * Os apelidos — só para as rotas em que o menu <b>não</b> responde.
 *
 * <p>Era uma lista de todas as telas, e por isso ela divergia: quem não estava aqui
 * aparecia com o nome da seção, e ninguém percebia até olhar o breadcrumb de uma tela
 * esquecida. Agora o rótulo do menu é a fonte, e sobram três casos:
 *
 * <ul>
 *   <li><b>/</b> e <b>/mundo</b>, que são telas sem item próprio na barra;</li>
 *   <li><b>/eu</b>, que não tem item de propósito — chega-se a ela pelo avatar;</li>
 *   <li><b>/marketing</b>, cujo rótulo curto na barra ("Visão geral") é bom lá e ruim
 *       aqui, onde o breadcrumb já diz a seção ao lado.</li>
 * </ul>
 */
const TITULOS: Record<string, string> = {
  '/': 'Home',
  '/mundo': 'Criação do Mundo',
  '/eu': 'Minha página',
  '/marketing': 'Marketing e Branding',
};

/**
 * Em que seção do menu cada rota mora. Sai do próprio {@link SECOES} para o breadcrumb
 * não poder discordar da sidebar: acrescentar um item lá já o descreve aqui.
 */
const SECAO_DA_ROTA = new Map(
  SECOES.flatMap((secao) => secao.itens.map((item) => [item.rota, secao.titulo] as const))
);

/**
 * A seção do menu em que um caminho mora — ou nada, para quem não tem item.
 *
 * <p>Separada de {@link tituloDa} de propósito: aquela responde o <b>breadcrumb</b>, e
 * para /roadmap devolve "Roadmap", que é o nome da tela e não o da seção. Usar uma
 * pela outra faria a barra abrir com tudo fechado justamente em quem chega pela Home.
 */
function secaoDa(caminho: string): string | undefined {
  return SECAO_DA_ROTA.get(caminho) ?? SECAO_DA_ROTA.get(caminho.replace(/\/\d+$/, ''));
}

/**
 * O nome que o breadcrumb mostra.
 *
 * <p>Em ordem: o apelido explícito em {@link TITULOS}, o <b>rótulo do item de menu</b>, e
 * só então o nome da seção.
 *
 * <p>O rótulo do menu entrou no meio para consertar um desencontro que durava: Dev Board,
 * Documentos e Season 1 apareciam como "Gestão de Projeto", enquanto Roadmap e Equipe
 * apareciam pelo próprio nome. A diferença não era decisão — era só quem tinha entrada em
 * {@code TITULOS} e quem não tinha. Ler o menu resolve na origem, e é o que o próprio
 * {@code menu.ts} diz ser a regra: renomear uma tela é mexer numa linha só.
 */
function tituloDa(caminho: string): string {
  if (TITULOS[caminho]) return TITULOS[caminho];

  // As rotas do mundo têm id no fim: /mundo/racas/3 é respondida por /mundo/racas.
  const doMenu = destinoDa(caminho);
  if (doMenu) return doMenu.rotulo;

  const semId = caminho.replace(/\/\d+$/, '');
  const daFicha = SECAO_DA_ROTA.get(semId);
  if (daFicha) return daFicha;

  // Regiões e Tipos não têm mais item no menu, mas as fichas continuam abrindo.
  if (caminho.startsWith('/mundo/')) return 'Criação do Mundo';
  return 'GG Pilot';
}

/**
 * O percentual que um item mede, ou nulo quando ele não mede nada — ou quando a
 * consulta ainda não voltou.
 *
 * <p>Nulo e zero são coisas diferentes aqui: nulo esconde a barra, e zero desenha uma
 * barra vazia. Enquanto o dado não chega, o certo é esconder — uma barra em 0% diria
 * que nada foi feito, que é uma afirmação, e não um "ainda não sei".
 */
function pctDaMedida(
  medida: ItemNav['medida'],
  progresso?: Progresso,
  roadmap?: Roadmap,
  quadro?: Quadro
): number | null {
  if (medida === 'progresso-da-season') {
    // Nulo quando nenhuma meta foi combinada: sem régua, fingir 0% seria dizer que o
    // time está atrasado num prazo que ninguém marcou.
    return progresso ? (progressoGeral(progresso.linhas)?.pct ?? null) : null;
  }
  if (medida === 'progresso-do-roadmap') {
    return roadmap ? roadmap.kpis.progressoPct : null;
  }
  if (medida === 'progresso-do-quadro') {
    // Nulo com o quadro vazio, e não 0%: uma barra zerada afirma que nada foi feito,
    // quando o certo é que ainda não há o que medir.
    if (!quadro) return null;
    return quadro.progresso.total === 0 ? null : quadro.progresso.pct;
  }
  return null;
}

export function AppShell() {
  const { usuario, sair } = useSessao();
  const local = useLocation();

  /**
   * Quais seções estão fechadas.
   *
   * <p>Elas existem porque os ícones cresceram: com quinze itens de 44px, a barra
   * aberta passa da altura de um laptop. Recolher é o que paga essa conta — e, de
   * quebra, quem está no Roadmap deixa de ver as sete fichas do Banco de Dados.
   *
   * <p>Na primeira visita tudo nasce fechado, menos a seção da página aberta: a barra
   * mostra onde você está, e não os quinze lugares aonde poderia ir. Depois disso a
   * escolha é sua, e sobrevive ao reload.
   */
  const [fechadas, setFechadas] = useState<Set<string>>(() => {
    try {
      const salvo = localStorage.getItem(SECOES_FECHADAS);
      if (salvo) return new Set(JSON.parse(salvo) as string[]);
    } catch {
      // preferência ilegível é preferência ausente: cai no padrão, sem quebrar a casca
    }
    const atual = secaoDa(window.location.pathname);
    return new Set(SECOES.map((s) => s.titulo).filter((t) => t !== atual));
  });

  /**
   * Quantas fichas há em cada coleção do mundo. É o que mostra, de relance, o que já
   * foi escrito e o que ainda está em branco — sem isso o menu é uma lista de portas
   * fechadas todas iguais.
   */
  const { data: contagem } = useQuery<Record<string, number>>({
    queryKey: ['mundo', 'contagem'],
    queryFn: () => api('/api/projetos/mistik-island/mundo/contagem'),
    staleTime: 30_000,
  });

  /**
   * O painel de Progresso, só para a barrinha do item "Season 1".
   *
   * <p>A régua vem de lá, e não de um número fixo no menu. Foi um número fixo aqui que
   * fez a barra dizer 16/49 enquanto a página dizia 16/21: duas medidas para a mesma
   * coisa, cada uma certa na própria régua e erradas juntas. Uma consulta a mais é
   * barata perto de dois números que se contradizem na mesma tela.
   */
  const { data: progresso } = useQuery<Progresso>({
    queryKey: ['mundo', 'progresso'],
    queryFn: () => api('/api/projetos/mistik-island/mundo/progresso'),
    staleTime: 30_000,
  });

  /**
   * O roadmap, para a barrinha do item "Roadmap".
   *
   * <p><b>A chave é a mesma que a página do Roadmap usa</b>, e não uma parecida. Com ela
   * o React Query serve as duas do mesmo cache: abrir a timeline não refaz a consulta
   * que a barra já fez, e uma frente marcada como concluída lá invalida a chave e
   * atualiza o percentual do menu junto. Uma chave própria aqui daria duas cópias do
   * mesmo roadmap em memória, e a do menu ficaria velha depois de cada edição.
   *
   * <p>O payload é o roadmap inteiro — mas é o mesmo que a página busca de qualquer
   * jeito, então o custo real de mostrar a barra é zero para quem visita a timeline, e
   * uma consulta a cada 30s para quem não visita.
   */
  const { data: roadmap } = useQuery<Roadmap>({
    queryKey: CHAVE_ROADMAP,
    queryFn: () => api(`/api/projetos/${SLUG}/roadmap`),
    staleTime: 30_000,
  });

  /**
   * O acervo, só para o número ao lado de "Documentos".
   *
   * <p>A listagem carrega só metadado (o conteúdo tem rota própria), então pedir o
   * acervo para ler um inteiro não baixa documento nenhum.
   */
  const { data: acervo } = useQuery<{ total: number }>({
    queryKey: ['documentos', SLUG],
    queryFn: () => api(`/api/projetos/${SLUG}/documentos`),
    staleTime: 30_000,
  });

  /** O mesmo, para o número ao lado de "Transcrições". */
  const { data: reunioes } = useQuery<{ total: number }>({
    queryKey: ['transcricoes'],
    queryFn: () => api(`/api/projetos/${SLUG}/transcricoes`),
    staleTime: 30_000,
  });

  /**
   * O quadro, para a barrinha do item "Dev Board".
   *
   * <p>Mesma chave da página, pelo mesmo motivo do roadmap acima: uma chave própria daria
   * duas cópias do quadro em memória, e a do menu ficaria velha a cada card movido.
   */
  const { data: quadro } = useQuery<Quadro>({
    queryKey: CHAVE_QUADRO,
    queryFn: () => api(`/api/projetos/${SLUG}/quadro`),
    staleTime: 30_000,
  });

  /**
   * As contagens das duas origens num mapa só.
   *
   * <p>O menu pergunta "quantos existem em {@code colecao}" e espera uma resposta. Sem
   * juntar aqui, o componente teria um caso especial para Documentos no meio do laço
   * que desenha os itens — e o total da seção, que soma as coleções, esqueceria dele.
   */
  const contagens: Record<string, number> = {
    ...(contagem ?? {}),
    documentos: acervo?.total ?? 0,
    transcricoes: reunioes?.total ?? 0,
    // Quem tem acesso ao GG Pilot. Sai do roadmap, que já traz o time para os avatares
    // das frentes — pedir a lista de usuários de novo seria uma consulta para repetir
    // um número que já está em memória.
    equipe: roadmap?.membros.length ?? 0,
  };

  const secaoAtual = secaoDa(local.pathname);

  /**
   * A gaveta do menu, em tela estreita.
   *
   * <p>Abaixo de 900px a barra não cabe ao lado do conteúdo, e a versão só-ícones que
   * existia perdia rótulo, contagem, seletor de projeto e o Sair — tudo o que faz a
   * barra valer o espaço. Em vez de encolher, ela sai da tela: o hambúrguer do
   * cabeçalho a traz inteira por cima do conteúdo, e o véu atrás devolve o lugar.
   *
   * <p>Em janela larga o estado é inerte: o CSS nem lê a classe `aberta` fora do
   * breakpoint, então abrir a gaveta no celular e alargar a janela não deixa rastro.
   */
  const [menuAberto, setMenuAberto] = useState(false);

  // Navegar fecha a gaveta: quem tocou num item quer a página, não o menu sobre ela.
  useEffect(() => {
    setMenuAberto(false);
  }, [local.pathname]);

  useEffect(() => {
    if (!menuAberto) return;
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuAberto(false);
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [menuAberto]);

  function alternarSecao(titulo: string) {
    setFechadas((antes) => {
      const novas = new Set(antes);
      novas.has(titulo) ? novas.delete(titulo) : novas.add(titulo);
      localStorage.setItem(SECOES_FECHADAS, JSON.stringify([...novas]));
      return novas;
    });
  }

  return (
    <div className="app">
      {/* O véu só existe com a gaveta aberta: é ele que recebe o toque de "fechar sem
          escolher nada", e um véu permanente roubaria o clique do conteúdo. */}
      {menuAberto && <div className="veu" onClick={() => setMenuAberto(false)} />}

      <aside className={`side${menuAberto ? ' aberta' : ''}`}>
        {/* Marca e projeto são um bloco só, fechado por uma régua: é o cabeçalho da
            barra, não três elementos soltos antes do menu. */}
        <header className="marca">
          <NavLink to="/" className="logo">
            {/* O "GG" do selo repete o "GG PILOT" ao lado — para quem lê por leitor de
                tela ele é ruído, não informação. */}
            <div className="mk" aria-hidden="true">
              GG
            </div>
            <div className="tt">
              <b>GG PILOT</b>
              <span>GOYAZ GAMES</span>
            </div>
          </NavLink>

          <SeletorDeProjeto />
        </header>

        <nav>
          {SECOES.map((secao) => {
            const fechada = fechadas.has(secao.titulo);
            const total = secao.itens.reduce(
              (soma, i) => soma + (i.colecao && !i.foraDoTotal ? (contagens[i.colecao] ?? 0) : 0),
              0
            );

            return (
              <div key={secao.titulo} style={{ '--c': secao.cor } as React.CSSProperties}>
                <button
                  type="button"
                  // Fechada, a seção da página atual continua se identificando: senão,
                  // recolher tudo tiraria a única pista de onde você está.
                  className={`sec${fechada ? '' : ' aberta'}${
                    secao.titulo === secaoAtual ? ' atual' : ''
                  }`}
                  onClick={() => alternarSecao(secao.titulo)}
                  aria-expanded={!fechada}
                >
                  <span className="seta">▶</span>
                  <span className="rot">{secao.titulo}</span>
                  {/* Fechada, a seção guarda o total: o número não some junto com os
                      itens que o formavam. */}
                  {fechada && total > 0 && <span className="tot">{total}</span>}
                </button>

                {!fechada &&
                  secao.itens.map((item) => {
                    const quantas = item.colecao ? (contagens[item.colecao] ?? 0) : null;
                    // O que o item mede, quando ele mede alguma coisa. É outra pergunta
                    // que a de quantas fichas existem, e por isso vem de outro campo.
                    //
                    // Cada medida lê o número da PRÓPRIA página — a da Season pela mesma
                    // função que desenha o selo do painel, a do Roadmap pelo mesmo KPI
                    // que a timeline mostra. É o que garante que o menu nunca diga um
                    // número diferente do que a tela mostra quando ela abrir.
                    const pct = pctDaMedida(item.medida, progresso, roadmap, quadro);
                    return (
                      <NavLink
                        key={item.rota}
                        to={item.rota}
                        className={({ isActive }) => `item sub${isActive ? ' on' : ''}`}
                      >
                        <span className="ic">
                          <IconeDoMenu icone={item.icone} titulo={item.rotulo} />
                        </span>
                        <span className="lbl">{item.rotulo}</span>

                        {/* Quem mede mostra o percentual; quem só conta mostra o
                            número. E
                            coleção vazia vira um ponto, não um "0": são significados
                            opostos, e mereciam desenhos diferentes. */}
                        {pct != null ? (
                          <span className="cont">{pct}%</span>
                        ) : (
                          quantas != null &&
                          (quantas > 0 ? (
                            <span className="cont">{quantas}</span>
                          ) : (
                            <span className="ponto" title="ainda vazio" />
                          ))
                        )}

                        {pct != null && (
                          <span className="prog" aria-hidden="true">
                            <i style={{ width: `${Math.min(100, pct)}%` }} />
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
              </div>
            );
          })}
        </nav>

        <div className="sfoot">
          {usuario && (
            <div className="quem">
              <Avatar
                className="av"
                nome={usuario.nome}
                cor={usuario.cor}
                url={usuario.avatar || null}
              />
              <span>
                <b>{usuario.nome}</b>
                {usuario.cargo}
              </span>
            </div>
          )}
          <button onClick={() => void sair()}>Sair</button>
        </div>
      </aside>

      <div className="main">
        <header className="chead">
          {/* Só aparece abaixo de 900px, onde a barra vira gaveta. */}
          <button
            type="button"
            className="hamb"
            aria-label="Abrir menu"
            aria-expanded={menuAberto}
            onClick={() => setMenuAberto(true)}
          >
            <span aria-hidden="true" />
          </button>
          <div className="crumb">
            Mistik Island <span style={{ opacity: 0.5 }}>›</span>
            <b>{tituloDa(local.pathname)}</b>
          </div>
          <div className="sp" style={{ flex: 1 }} />
          {/* O que veio até você fica aqui, fora da barra lateral: a lateral responde
              "para onde ir", e isto responde "o que aconteceu". */}
          <AvatarComNotificacoes />
        </header>
        <div className="cbody">
          {/* O cerco fica AQUI, e não em volta do app: assim uma tela que quebra vira um
              retângulo com explicação, e a barra lateral continua de pé para levar a
              pessoa a outro lugar. Em volta do app, o resultado seria a mesma janela
              branca que ele existe para evitar. */}
          <TelaQueQuebrou rota={local.pathname}>
            <Outlet />
          </TelaQueQuebrou>
        </div>
      </div>
    </div>
  );
}
